/**
 * REM-MOB-03 (full) — the min-version force-update GATE.
 *
 * Wraps the app surface at the root (app/_layout.tsx). Reads the running binary version from
 * expo-constants and the backend-declared minimum from the public /health endpoint, then uses the
 * pure `isUpdateRequired` decision (src/config/versionGate.ts) to decide whether to block:
 *   below minimum  -> render <ForceUpdateScreen> (children never mount)
 *   equal / newer  -> render children
 *   no minimum / not-yet-loaded -> render children (fail OPEN: never trap a user on a network hiccup)
 *
 * Fail-open is deliberate: a down /health or a missing field must not lock everyone out. The gate only
 * ever CLOSES on a definite "your version < the declared minimum" answer.
 *
 * NEVER imports @smartbox/* (mobile invariant 1).
 */
import type { ReactNode } from 'react';
import Constants from 'expo-constants';
import { useQuery } from '@tanstack/react-query';
import { isUpdateRequired } from '../config/versionGate';
import { fetchMinSupportedVersion } from '../api/appConfig';
import { ForceUpdateScreen } from './ForceUpdateScreen';

/** The running binary version (app.json `expo.version`), or a safe floor if unavailable. */
export function currentAppVersion(): string {
  return Constants.expoConfig?.version ?? '0.0.0';
}

export function VersionGate({ children }: { children: ReactNode }) {
  const current = currentAppVersion();
  const minQuery = useQuery({
    queryKey: ['min-supported-version'],
    queryFn: fetchMinSupportedVersion,
    // The minimum changes rarely; poll infrequently and keep the last answer across the session.
    staleTime: 5 * 60_000,
    // Fail open: a network error leaves min undefined -> gate stays open (no lockout).
    retry: 1,
  });

  if (isUpdateRequired(current, minQuery.data)) {
    return <ForceUpdateScreen current={current} required={minQuery.data ?? ''} />;
  }
  return <>{children}</>;
}
